const React = require("react");
const T = require("prop-types");
const { default: Dialog } = require("material-ui/Dialog");
const { default: RaisedButton } = require("material-ui/RaisedButton");
const { default: FlatButton } = require("material-ui/FlatButton");
const SelectField = require("material-ui/SelectField").default;
const MenuItem = require("material-ui/MenuItem").default;
const Classes = require("./styles.scss");
const DownloadIcon = require("material-ui/svg-icons/file/file-download")
    .default;
const StarIcon = require("material-ui/svg-icons/action/grade").default;
const { downloadImageLink, downloadPdfLink } = require("utils/asset-api");
const ColorDisplay = require("utils/croquis-measurements").fields.general.fields
    .color.options;

const internals = {};

internals.poseDisplay = {
    armsDown: "Arms Down (Basic)",
    handOnHip: "One Hand on Hip (Premium)"
};

internals.downloadOptionSelectHeader = {
    fontSize: "22px",
    color: "#E07056",
    fontWeight: 900,
    top: "30px"
};

const ContentHelpers = require("utils/content-helpers");

module.exports = class DownloadDialog extends React.PureComponent {
    static propTypes = {
        croquis: T.shape({
            id: T.number.isRequired,
            downloadPackage: T.shape({
                name: T.oneOf(["basic", "premium"]).isRequired,
                features: T.arrayOf(T.oneOf(["pose", "backView", "lineColor"]))
                    .isRequired
            }),
            hasBackView: T.bool.isRequired,
            measurements: T.shape({
                flags: T.shape({
                    lineColor: T.oneOf(Object.keys(ColorDisplay)).isRequired
                })
            }).isRequired,
            title: T.string.isRequired
        }).isRequired,
        dialogOpen: T.bool.isRequired,
        dialogClose: T.func.isRequired,
        onUpgradeClick: T.func.isRequired,
        renderingOptions: T.object.isRequired,
        token: T.string.isRequired
    };

    constructor(props, context) {
        super(props, context);

        if (!props.croquis) {
            return null;
        }

        // TODO Currently, all possible purchase options
        // Should these be tied to features available on croquis?
        this.state = {
            lineColor: null,
            pose: null
        };

        this.prepareDownloadParams = this._prepareDownloadParams.bind(this);
        this.setField = this._setField.bind(this);
        this.renderActions = this._renderActions.bind(this);
        this.renderDownloadOptions = this._renderDownloadOptions.bind(this);
    }

    // Bic'd / dumbed down from strange-forms' proposeNew method
    _setField(field) {
        return (...args) => {
            // b/c lazy, tightly coupled to select fields for now
            const value = this.getSelectVal(...args);
            this.setState({
                [field]: value
            });
        };
    }

    getSelectVal(ev, index, value) {
        return value;
    }

    _prepareDownloadParams(amendments) {
        const { croquis } = this.props;
        const options = this.state;
        const prepared = amendments
            ? Object.assign({}, options, amendments)
            : Object.assign({}, options);

        // Gross, hacky bandaid to sync state defaults with UI ones
        // for download options
        if (croquis.downloadPackage.name === "premium") {
            if (!prepared.lineColor) {
                prepared.lineColor = croquis.measurements.flags.lineColor;
            }
            if (!prepared.pose) {
                prepared.pose = "handOnHip";
            }
        }

        // Sanity check to prevent setting a pose for basic downloads
        // Server defaults to armsDown pose, so we don't have set a pose
        // Mostly to make explicit that the basic download
        // package doesn't allow changing pose
        if (croquis.downloadPackage.name === "basic") {
            delete prepared.pose;
        }

        return prepared;
    }

    _renderActions() {
        const { croquis, dialogClose, onUpgradeClick, token } = this.props;
        const { downloadPackage } = croquis;

        return (
            <div className={Classes.buttonContainer}>
                {downloadPackage.name !== "premium" && (
                    <RaisedButton
                        primary
                        className={Classes.modalButton}
                        label={
                            <span>
                                <StarIcon
                                    color="white"
                                    style={{
                                        position: "relative",
                                        top: ".6rem",
                                        right: ".5rem"
                                    }}
                                />
                                Upgrade to Premium (1 Credit)
                            </span>
                        }
                        onClick={() => {
                            dialogClose();
                            return onUpgradeClick(croquis.id);
                        }}
                        labelStyle={{ textTransform: "inherit" }}
                    />
                )}
                <RaisedButton
                    label="Download Project Planner PDF"
                    icon={<DownloadIcon />}
                    className={Classes.modalButton}
                    containerElement={
                        <a
                            href={`${downloadPdfLink(
                                croquis.id,
                                token,
                                this.prepareDownloadParams()
                            )}`}
                        />
                    }
                    onClick={() => dialogClose()}
                />
                <RaisedButton
                    label="Download Front View Image"
                    icon={<DownloadIcon />}
                    className={Classes.modalButton}
                    containerElement={
                        <a
                            href={`${downloadImageLink(
                                croquis.id,
                                token,
                                this.prepareDownloadParams({ view: "front" })
                            )}`}
                        />
                    }
                    onClick={() => dialogClose()}
                />
                {/* Premium downloads should ALWAYS support back view; likely a bug if this condition is false */}
                {croquis.hasBackView && (
                    <RaisedButton
                        label="Download Back View Image"
                        icon={<DownloadIcon />}
                        className={Classes.modalButton}
                        containerElement={
                            <a
                                href={`${downloadImageLink(
                                    croquis.id,
                                    token,
                                    this.prepareDownloadParams({ view: "back" })
                                )}`}
                            />
                        }
                        onClick={() => dialogClose()}
                    />
                )}
                <FlatButton
                    label="Close"
                    className={Classes.modalButton}
                    onClick={() => dialogClose()}
                />
            </div>
        );
    }

    // Intention: Render only the download options configured on our purchase
    // API is responsible for ensuring that these options correspond
    // to what the user paid for (purchase type) AND what are available from the renderer
    // at the version used for the model, so this form exposes only
    // the options our API will allow for configuring a given Purchase's download
    // i.e. will allow as input to the renderer / PDF generator to modify output files
    _renderDownloadOptions() {
        const { croquis, renderingOptions } = this.props;
        const { downloadPackage } = croquis;

        return (
            <div>
                <SelectField
                    className={Classes.downloadOption}
                    key={"lineColorDownloadOption"}
                    value={
                        this.state.lineColor ||
                        croquis.measurements.flags.lineColor
                    }
                    onChange={this.setField("lineColor")}
                    floatingLabelText="Choose Line Color"
                    floatingLabelStyle={internals.downloadOptionSelectHeader}
                    autoWidth
                    disabled={!downloadPackage.features.includes("lineColor")}
                >
                    {Object.keys(renderingOptions.LINE_COLORS).map(color => {
                        return (
                            <MenuItem
                                key={color}
                                value={color}
                                primaryText={ColorDisplay[color]}
                            />
                        );
                    })}
                </SelectField>
                <SelectField
                    className={Classes.downloadOption}
                    key={"poseDownloadOption"}
                    value={this.state.pose || "handOnHip"}
                    onChange={this.setField("pose")}
                    floatingLabelText="Choose Pose"
                    floatingLabelStyle={internals.downloadOptionSelectHeader}
                    autoWidth
                    disabled={!downloadPackage.features.includes("pose")}
                    selectionRenderer={(selectionVal, selectedMenuItem) => {
                        // Sucks, sorry! Hardcoded for extra simplicity, unclear if any
                        // more flexibility needed here.

                        const labelText = selectedMenuItem.props.primaryText;
                        if (selectionVal === "handOnHip") {
                            return (
                                <span>
                                    <StarIcon
                                        style={{ verticalAlign: "middle" }}
                                    />{" "}
                                    {labelText}
                                </span>
                            );
                        }

                        return labelText;
                    }}
                >
                    {renderingOptions.POSES.map(pose => {
                        return (
                            <MenuItem
                                key={pose}
                                leftIcon={
                                    pose === "handOnHip" && (
                                        <StarIcon
                                            style={{ verticalAlign: "middle" }}
                                        />
                                    )
                                }
                                value={pose}
                                primaryText={internals.poseDisplay[pose]}
                            />
                        );
                    })}
                </SelectField>
            </div>
        );
    }

    render() {
        const { croquis, dialogClose, dialogOpen } = this.props;

        if (!croquis) {
            return null;
        }

        const { downloadPackage } = croquis;

        return (
            <Dialog
                title={`${croquis.title} — ${ContentHelpers.capitalize(
                    downloadPackage.name
                )} Download Options`}
                actions={this.renderActions()}
                open={dialogOpen}
                modal
                onRequestClose={dialogClose}
                autoScrollBodyContent
                autoDetectWindowHeight
                paperClassName={`${Classes.modal} ${Classes.downloadModal}
              ${
                  downloadPackage.name === "basic"
                      ? Classes.basicDownloadContent
                      : Classes.premiumDownloadContent
              }`}
                overlayClassName={Classes.overlay}
                style={{ marginTop: "0px !important" }}
            >
                {downloadPackage.name !== "basic" &&
                    this.renderDownloadOptions()}
            </Dialog>
        );
    }
};



// WEBPACK FOOTER //
// ./src/components/Dialog/DownloadDialog.js